import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { forkJoin } from 'rxjs';
import { environment } from '../../../environments/environment';

import { BiblioDetailService } from './BiblioDetailService';
import { BiblioDetailMapper } from './biblio-detail.mapper';
import { BrowseIndexService } from '../../features/browse-index/services/browse-index.service';
import { BiblioMapper } from '../../features/browse-index/mappers/biblio.mapper';
import { BrowseIndexItem } from '../../features/browse-index/models';
import { DEFAULT_SORT } from '../../features/browse-index/models';
import { BiblioDetailModel } from '../../types/types';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FooterComponent } from '../../shared/footer/footer.component';
import { FlipbookComponent } from '../../shared/components/flipbook/flipbook.component';
import { IndexCardComponent } from '../../features/browse-index/components/index-card/index-card.component';
import { BreadCrump } from '../../shared/bread-crump/bread-crump';

type TabKey = 'book' | 'description' | 'copies';

@Component({
  selector: 'app-index-details',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule, FooterComponent, FlipbookComponent, IndexCardComponent, BreadCrump],
  templateUrl: './index-details.html',
  styleUrl: './index-details.scss',
})
export class IndexDetails implements OnInit {
  private route = inject(ActivatedRoute);
  private service = inject(BiblioDetailService);
  private sanitizer = inject(DomSanitizer);
  private browseService = inject(BrowseIndexService);

  /** Other catalog items shown under "Other materials" (current item excluded) */
  relatedItems = signal<BrowseIndexItem[]>([]);
  public translate = inject(TranslateService);

  /** Resolved biblio + holdings display model */
  biblio = signal<BiblioDetailModel | null>(null);
  safePdfUrl = signal<SafeResourceUrl | null>(null);
  pdfUrl = signal<string | null>(null);
  loading = signal(true);
  error = signal<string | null>(null);

  /** Active tab: 'book' | 'description' | 'copies' */
  activeTab = signal<TabKey>('book');
  activeMaterialTab = signal<'author' | 'topic'>('author');

  readonly tabs: { key: TabKey; label: string }[] = [
    { key: 'book',        label: 'indexDetails.tabs.bibliographics' },
    { key: 'description', label: 'indexDetails.tabs.description' },
    { key: 'copies',      label: 'indexDetails.tabs.copies' },
  ];

  readonly fallbackImage = '/media/images/custom/defualt-img.jpg';

  ngOnInit(): void {
    // Re-load when only the :id changes (e.g. clicking a related card reuses this component)
    this.route.paramMap.subscribe(() => this.load());
  }

  private load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.relatedItems.set([]);
    window.scrollTo({ top: 0 });

    // Supports both route param (:id) and query param (?id=...)
    const paramId = this.route.snapshot.paramMap.get('id');
    const queryId = this.route.snapshot.queryParamMap.get('id');
    const rawId = paramId || queryId;

    if (!rawId) {
      this.error.set('indexDetails.errors.noRecordId');
      this.loading.set(false);
      return;
    }

    const id = +rawId;

    forkJoin({
      biblio: this.service.fetchBiblio(id),
      items: this.service.fetchItems(id),
    }).subscribe({
      next: ({ biblio, items }) => {
        const model = BiblioDetailMapper.toModel(biblio, items);
        this.biblio.set(model);
        if (model.files && model.files.length > 0) {
          let fileUrl = model.files[0].filePath;
          if (!fileUrl.startsWith('http')) {
            fileUrl = environment.baseUrl + '/Files/' + fileUrl;
          }
          this.safePdfUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(fileUrl));
          this.pdfUrl.set(fileUrl);
        }
        this.loading.set(false);
        this.loadRelated(id);
      },
      error: () => {
        this.error.set('indexDetails.errors.loadFailed');
        this.loading.set(false);
      },
    });
  }

  private loadRelated(currentId: number): void {
    this.browseService
      .fetchBiblios({ search: '', filters: [], page: 1, pageSize: 4, sort: DEFAULT_SORT })
      .subscribe({
        next: res =>
          this.relatedItems.set(
            BiblioMapper.toModelList(res.data).filter(i => i.id !== currentId).slice(0, 3)
          ),
        error: () => this.relatedItems.set([]),
      });
  }

  setTab(key: TabKey): void {
    this.activeTab.set(key);
  }

  setMaterialTab(tab: 'author' | 'topic'): void {
    this.activeMaterialTab.set(tab);
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = this.fallbackImage;
  }

  /** Map status value to translation key */
  statusLabel(status: string): string {
    const map: Record<string, string> = {
      'available': 'indexDetails.status.available',
      'checked-out': 'indexDetails.status.checkedOut',
      'lost': 'indexDetails.status.lost',
      'not-for-loan': 'indexDetails.status.notForLoan',
    };
    return map[status] ?? status;
  }

  getFileUrl(filePath: string): string {
    if (!filePath) return '';
    if (filePath.startsWith('http')) return filePath;
    return environment.baseUrl + '/Files/' + filePath;
  }

  viewFile(filePath: string): void {
    const url = this.getFileUrl(filePath);
    if (url) {
      window.open(url, '_blank');
    }
  }
}
